<template>
  <div :style="style.container">
    <div :style="style.headerContainer">
      <SearchExpertBoxPrimary
        v-model="tags"
        :disabled="isSearching"
        :initInput="initInput"
        placeholder="Search experts"
        @filled="searchBoxFilled = true"
        @unfilled="searchBoxFilled = false"
        @focus="searchBoxFocus = true"
        @unfocus="searchBoxFocus = false"
        @search-by-person-name="searchExpertByPersonName"
        @search-by-skill-name="searchExpertBySkillName"
        :style="getSearchBoxStyle"
      />
    </div>
    <div :style="style.searchResultsContainer">
      <template v-if="isSearching">
        <BeatLoaderPrimary :style="style.loader" />
      </template>
      <template v-else-if="length > 0">
        <TextRegular>
          {{ length }} result{{ length > 1 ? 's' : '' }}
        </TextRegular>
        <div
          v-for="(expert, index) in displayedExperts"
          :key="index"
          :style="style.searchedUserContainer"
        >
          <SearchedUserSimple
            v-if="!searchBySkill"
            :expert="expert"
            :style="style.searchedUser"
          />
          <SearchedUserVertical
            v-else-if="verticalLayout"
            :expert="expert"
            :style="style.searchedUser"
          />
          <SearchedUserHorizontal
            v-else
            :expert="expert"
            :style="style.searchedUser"
          />
        </div>
        <PaginationPrimary
          v-if="length > 0"
          v-model="page"
          :pageNum="nPages"
          :style="style.pagination"
        />
      </template>
      <template v-else>
        <DataPlaceholderSecondary :style="style.placeholder">
          <template v-slot:top>
            No result found.<br>Try using different search words.
          </template>
        </DataPlaceholderSecondary>
      </template>
    </div>
  </div>
</template>

<script>
  import ApiExpert from '@js/api/expert'
  import DataPlaceholderSecondary
    from '@modules/v2/placeholder/data-placeholder-secondary'
  import BeatLoaderPrimary from '@modules/v2/loader/beat-loader-primary'
  import PaginationPrimary from '@modules/pagination/pagination-primary'
  import SearchedUserHorizontal from '@search/modules/searched-user-horizontal'
  import SearchedUserSimple from '@search/modules/searched-user-simple'
  import SearchedUserVertical from '@search/modules/searched-user-vertical'
  import SearchExpertBoxPrimary from '@modules/v2/input/search-expert-box-primary'
  import TextRegular from '@modules/v2/text/text-regular'
  import { Layout } from '@js/styles'
  import { PAGE_SIZES, SEARCH_EXPERT_PARAMS, Z_INDEXES } from '@js/constants'
  import { mapState } from 'vuex'
  import { parseRouteQuery } from '@js/search-utils'

  export default {
    name: 'SearchView',
    components: {
      BeatLoaderPrimary,
      DataPlaceholderSecondary,
      PaginationPrimary,
      SearchedUserHorizontal,
      SearchedUserSimple,
      SearchedUserVertical,
      SearchExpertBoxPrimary,
      TextRegular,
    },
    data: function () {
      return {
        experts: [],
        initInput: '',
        initTags: [],
        input: '',
        isSearching: false,
        nPages: 0,
        page: 1,
        pageSize: PAGE_SIZES.DEFAULT,
        searchBoxFilled: false,
        searchBoxFocus: false,
        searchBySkill: false,
        tags: [],
        verticalLayout: false,
        verticalLayoutWidthThresh: 860,
        style: {
          container: {
            margin: Layout.container.primary.margin,
            maxWidth: Layout.container.primary.maxWidth,
            padding: Layout.space.lg,
            width: '100%',
          },
          headerContainer: {
            height: '68px',
            position: 'relative',
            width: '100%',
          },
          loader: {
            height: '320px',
          },
          pagination: {
            display: 'flex',
            justifyContent: 'center',
            margin: `${Layout.space.lg} 0 0 0`,
            width: '100%',
          },
          placeholder: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            margin: '48px 0',
          },
          searchedUserContainer: {
            width: '100%',
          },
          searchedUser: {
            margin: `${Layout.space.md} 0 0 0`,
          },
          searchResultsContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          },
        },
      };
    },
    mounted: async function () {
      this.verticalLayout = this.windowWidth < this.verticalLayoutWidthThresh;

      const searchParams = await parseRouteQuery(this.$route.query, false);

      if (searchParams != null) {
        try {
          const { searchInput, searchBySkill } = await searchParams;
          this.initInput = await searchBySkill ? '' : searchInput[0];
          this.initTags = await searchBySkill ? searchInput : [];
          this.tags = [...this.initTags];
          this.searchBySkill = await searchBySkill;
          await this.searchExpert(searchInput, searchBySkill);
        } catch (error) {
          console.error(error);
        }
      }
    },
    methods: {
      clearSearchCache: function () {
        this.experts = [];
        this.isSearching = false;
        this.nPages = 0;
        this.page = 1;
      },
      searchExpert: async function (searchInput, searchBySkill) {
        try {
          this.isSearching = await true;

          const payload = await {
            bySkill: searchBySkill,
            inputData: searchInput,
          };
          const query = await {
            page: this.page,
            pageSize: this.pageSize,
          };

          const api = await new ApiExpert();
          const res = await api.post(payload, query);
          this.nPages = await res.data.nPages;
          this.experts = await res.data.results;
          this.length = await res.data.length;

          this.isSearching = await false;
        } catch (error) {
          this.clearSearchCache();
          this.$store.commit('notification/setWarning', 'Search failed');
          console.error(error);
        }
      },
      searchExpertByPersonName: function (input) {
        if (this.searchBySkill || input !== this.initInput) {
          const query = {
            by: SEARCH_EXPERT_PARAMS.MODE.BY_PERSON_NAME,
            tag: input,
          };
          this.$router.push({
            name: 'search',
            query: query
          });
        }
      },
      searchExpertBySkillName: function (tags) {
        if (
          !this.searchBySkill ||
          JSON.stringify(this.initTags) !== JSON.stringify(tags)
        ) {
          const query = {
            by: SEARCH_EXPERT_PARAMS.MODE.BY_SKILL_NAME,
            tag: tags,
          };
          this.$router.push({
            name: 'search',
            query: query,
          });
        }
      },
    },
    computed: {
      ...mapState({
        windowWidth: state => state.window.width,
      }),
      displayedExperts: function () {
        // const indexStart = (this.page - 1) * this.pageSize;
        // return [...this.experts].splice(indexStart, this.pageSize);
        return this.experts
      },
      getSearchBoxStyle: function () {
        return {
          flex: 1,
          margin: `6px 0 0 0`,
          maxWidth: this.searchBoxFocus || this.searchBoxFilled ? '480px' : '280px',
          zIndex: Z_INDEXES.SEARCH.SEARCH_BOX,
        };
      },
    },
    watch: {
      page(newPage, oldPage) {
        this.searchExpert(this.tags, this.searchBySkill);
      },
      windowWidth: function (newVal, oldVal) {
        this.verticalLayout = newVal < this.verticalLayoutWidthThresh;
      },
    },
  };
</script>